<template>
  <div class="exam-preview-content">
    <LessonPreview
      v-if="
        exam &&
        exam.productType &&
        exam.productType.value === 'lesson'
      "
      :key="lessonKey"
      :examVaultId="exam._id"
    />
    <div
      v-else-if="exam && exam.type === 'video'"
      class="video-preview-container"
      style="padding: 16px;"
    >
      <!-- Instruction content -->
      <div
        v-if="exam.content"
        class="video-instruction"
        style="margin-bottom: 20px;"
      >
        <h4 style="font-weight: bold; margin-bottom: 8px;">Nội dung hướng dẫn</h4>
        <div v-html="exam.content" />
      </div>

      <!-- Video embed -->
      <div v-if="exam.videoUrl" style="margin-bottom: 20px;">
        <h4 style="font-weight: bold; margin-bottom: 8px;">Video</h4>
        <div v-if="youtubeEmbedUrl(exam.videoUrl)" class="video-frame">
          <iframe
            :src="youtubeEmbedUrl(exam.videoUrl)"
            frameborder="0"
            allowfullscreen
            style="width: 100%; height: 400px; border-radius: 8px;"
          />
        </div>
        <div v-else>
          <a :href="exam.videoUrl" target="_blank" rel="noopener" style="color: #1cab36; word-break: break-all;">
            {{ exam.videoUrl }}
          </a>
        </div>
      </div>

      <!-- Attachment -->
      <div v-if="exam.attachment" style="margin-top: 8px;">
        <h4 style="font-weight: bold; margin-bottom: 8px;">Tệp đính kèm</h4>
        <img
          v-if="isImage(exam.attachment)"
          :src="exam.attachment | media"
          style="max-width: 100%; border-radius: 8px;"
        />
        <a
          v-else
          :href="exam.attachment | media"
          target="_blank"
          style="color: #1cab36;"
        >Xem tệp đính kèm</a>
      </div>

      <div v-if="!exam.videoUrl && !exam.attachment && !exam.content">
        <el-empty description="Chưa có nội dung video" />
      </div>
    </div>

    <!-- Vocabulary preview -->
    <div v-else-if="exam && exam.type === 'vocabulary'" style="padding: 20px;">
      <h4 style="color: #1cab36; font-weight: bold; margin-bottom: 15px;">Danh sách từ vựng ({{ (exam.vocabularies || []).length }})</h4>
      <div v-if="(exam.vocabularies || []).length === 0" style="text-align: center; color: #999; padding: 40px 0;">
        Chưa có từ vựng nào.
      </div>
      <div v-else style="display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 15px;">
        <div v-for="(v, idx) in exam.vocabularies" :key="idx" style="border: 1px solid #e0e0e0; border-radius: 8px; padding: 12px; background: #fafafa;">
          <div style="font-weight: bold; color: #1cab36; font-size: 16px;">
            {{ v.word }}
            <span v-if="v.type" style="font-size: 12px; color: #888; font-weight: normal;">({{ v.type }})</span>
          </div>
          <div v-if="v.ipa" style="color: #666; font-style: italic; font-size: 13px; margin-bottom: 6px;">/{{ v.ipa }}/</div>
          <div style="font-size: 14px; color: #333;">{{ v.englishMean || v.meaning }}</div>
        </div>
      </div>
    </div>

    <!-- Conversation preview -->
    <div v-else-if="exam && exam.type === 'conversation'" style="padding: 20px;">
      <h4 style="color: #1cab36; font-weight: bold; margin-bottom: 15px; text-align: center;">Xem trước hội thoại</h4>
      <div v-if="(exam.conversations || []).length === 0" style="text-align: center; color: #999; padding: 40px 0;">
        Chưa có nội dung hội thoại.
      </div>
      <div v-else style="background: #f0f2f5; padding: 20px; border-radius: 12px; max-height: 500px; overflow-y: auto;">
        <div v-for="(conv, idx) in exam.conversations" :key="idx" style="margin-bottom: 20px; display: flex; width: 100%;" :style="{ justifyContent: conv.user === 'A' ? 'flex-start' : 'flex-end' }">
          <div v-if="conv.user === 'A'" style="margin-right: 10px; width: 36px; height: 36px; border-radius: 50%; background: #1cab36; color: white; display: flex; align-items: center; justify-content: center; font-weight: bold; flex-shrink: 0;">A</div>
          <div style="max-width: 70%;">
            <div v-for="(line, lIdx) in (conv[conv.user] || [])" :key="lIdx" :style="{ background: conv.user === 'A' ? 'white' : '#1cab36', color: conv.user === 'A' ? '#333' : 'white', padding: '10px 15px', borderRadius: '15px', boxShadow: '0 1px 2px rgba(0,0,0,0.1)', marginBottom: '10px' }">
              <div style="font-size: 16px; margin-bottom: 5px;">{{ line[0] }}</div>
              <div v-if="line[1]" :style="{ fontSize: '14px', color: conv.user === 'A' ? '#888' : '#e0f2f1' }">{{ line[1] }}</div>
              <div v-if="line[2]" style="margin-top: 8px;">
                 <audio controls :src="line[2]" style="height: 30px; width: 200px;"></audio>
              </div>
            </div>
          </div>
          <div v-if="conv.user === 'B'" style="margin-left: 10px; width: 36px; height: 36px; border-radius: 50%; background: #ff9800; color: white; display: flex; align-items: center; justify-content: center; font-weight: bold; flex-shrink: 0;">B</div>
        </div>
      </div>
    </div>

    <ExamPreview
      v-else-if="exam"
      :questionGroups="exam?.questionGroups || []"
    />
  </div>
</template>

<script>
import ExamPreview from './ExamPreview.vue'
import LessonPreview from './LessonPreview.vue'

export default {
  name: 'ExamPreviewContent',
  components: { ExamPreview, LessonPreview },

  props: {
    exam: { type: Object, default: null },
    lessonKey: { type: Number, default: 0 }
  },

  methods: {
    youtubeEmbedUrl(url) {
      if (!url) return null
      const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
      const match = url.match(regExp)
      return (match && match[2].length === 11)
        ? 'https://www.youtube.com/embed/' + match[2]
        : null
    },

    isImage(url) {
      if (!url) return false
      return url.match(/\.(jpeg|jpg|gif|png)$/i) != null
    }
  }
}
</script>
